<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AccountVoicesAPI from 'dashboard/api/accountVoices';
import TtsAPI from 'dashboard/api/tts';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));

const records = ref([]);
const isLoading = ref(true);
const isCloning = ref(false);
const showCloneForm = ref(false);
const cloneName = ref('');
const cloneFiles = ref([]);
const fileInput = ref(null);
const isDragging = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedVoice = ref({});

// --- gravação pelo microfone ---
const isRecording = ref(false);
const recordSeconds = ref(0);
let mediaRecorder = null;
let mediaStream = null;
let recordTimer = null;
let recordedChunks = [];

// --- prévia de áudio da lista ---
const playingId = ref(null);
let audioEl = null;

let pollTimer = null;

const deleteMessage = computed(() => ` ${selectedVoice.value.name}?`);

const hasProcessing = computed(() =>
  records.value.some(voice => voice.status === 'processing')
);

const canSubmit = computed(
  () =>
    !isCloning.value && !!cloneName.value.trim() && cloneFiles.value.length > 0
);

const statusLabelKey = {
  ready: 'ACCOUNT_VOICES.STATUS_READY',
  processing: 'ACCOUNT_VOICES.STATUS_PROCESSING',
  failed: 'ACCOUNT_VOICES.STATUS_FAILED',
};

const statusPillClass = {
  ready: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
  processing: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
  failed: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
};

// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const statusLabel = status =>
  t(statusLabelKey[status] || statusLabelKey.processing);
const statusClass = status =>
  statusPillClass[status] || statusPillClass.processing;

const usageLabel = voice => {
  if (voice.status !== 'ready') return '';
  if (voice.used_by?.length) {
    return t('ACCOUNT_VOICES.LIST.USED_BY', {
      agents: voice.used_by.join(', '),
    });
  }
  return t('ACCOUNT_VOICES.LIST.NOT_USED');
};

const formatDate = dateString =>
  dateString
    ? new Date(dateString).toLocaleDateString(browserLocale.value)
    : '-';

const formatSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatTimer = secs => {
  const m = String(Math.floor(secs / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const fetchVoices = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  try {
    const { data } = await AccountVoicesAPI.get();
    records.value = data.payload;
  } catch (error) {
    if (!silent) useAlert(t('ACCOUNT_VOICES.LIST.FETCH_ERROR'));
  } finally {
    if (!silent) isLoading.value = false;
  }
};

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(() => {
    if (hasProcessing.value) fetchVoices({ silent: true });
  }, 5000);
};

// --- amostras (upload + drag/drop) ---
// Áudio baixado do WhatsApp no Windows vem como .mpeg (tipo video/mpeg) ou
// sem tipo — aceita também por extensão, senão o arquivo some do seletor.
const AUDIO_EXT = /\.(mp3|mpeg|mpga|ogg|oga|opus|m4a|aac|wav|webm)$/i;
const addFiles = fileList => {
  const audios = Array.from(fileList || []).filter(
    f =>
      f.type.startsWith('audio/') ||
      f.type === 'video/mpeg' ||
      AUDIO_EXT.test(f.name || '')
  );
  if (audios.length) cloneFiles.value = [...cloneFiles.value, ...audios];
};

const onFilesSelected = event => {
  addFiles(event.target.files);
  if (fileInput.value) fileInput.value.value = '';
};

const onDrop = event => {
  isDragging.value = false;
  addFiles(event.dataTransfer?.files);
};

const openFilePicker = () => fileInput.value?.click();

const removeSample = index => {
  cloneFiles.value = cloneFiles.value.filter((_, i) => i !== index);
};

// --- gravação ---
const stopStream = () => {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop());
    mediaStream = null;
  }
};

const startRecording = async () => {
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    useAlert(t('ACCOUNT_VOICES.CLONE.MIC_DENIED'));
    return;
  }
  recordedChunks = [];
  mediaRecorder = new MediaRecorder(mediaStream);
  mediaRecorder.ondataavailable = e => {
    if (e.data.size) recordedChunks.push(e.data);
  };
  mediaRecorder.onstop = () => {
    // Normaliza o mime sem codecs (ex.: audio/webm;codecs=opus → audio/webm)
    // porque o backend valida o content_type por igualdade exata.
    const rawType = mediaRecorder.mimeType || 'audio/webm';
    const type = rawType.split(';')[0];
    const ext = type.split('/')[1] || 'webm';
    const blob = new Blob(recordedChunks, { type });
    const base = t('ACCOUNT_VOICES.CLONE.RECORDED_NAME');
    const idx =
      cloneFiles.value.filter(f => f.name.startsWith(base)).length + 1;
    const file = new File([blob], `${base}-${idx}.${ext}`, { type });
    cloneFiles.value = [...cloneFiles.value, file];
    stopStream();
  };
  mediaRecorder.start();
  isRecording.value = true;
  recordSeconds.value = 0;
  recordTimer = setInterval(() => {
    recordSeconds.value += 1;
  }, 1000);
};

const stopRecording = () => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
  isRecording.value = false;
  if (recordTimer) {
    clearInterval(recordTimer);
    recordTimer = null;
  }
};

// --- prévia ---
const stopAudio = () => {
  if (audioEl) {
    audioEl.pause();
    audioEl = null;
  }
  playingId.value = null;
};

const previewVoice = async voice => {
  if (playingId.value === voice.id) {
    stopAudio();
    return;
  }
  playingId.value = voice.id;
  try {
    const { data: blob } = await TtsAPI.preview({
      voice: voice.voice_id,
      // Voz clonada no Voice Studio (navivoice) não existe na ElevenLabs —
      // a prévia tem que ir pro provedor que a criou.
      provider: voice.provider || 'elevenlabs',
      text: t('AI_AGENTS.TTS.PREVIEW_TEXT'),
    });
    const url = URL.createObjectURL(blob);
    if (audioEl) audioEl.pause();
    audioEl = new Audio(url);
    audioEl.onended = () => {
      playingId.value = null;
      URL.revokeObjectURL(url);
    };
    audioEl.onerror = () => {
      playingId.value = null;
      URL.revokeObjectURL(url);
    };
    audioEl.play();
  } catch {
    playingId.value = null;
    useAlert(t('ACCOUNT_VOICES.LIST.PREVIEW_ERROR'));
  }
};

// --- clonar ---
const resetCloneForm = () => {
  cloneName.value = '';
  cloneFiles.value = [];
  if (isRecording.value) stopRecording();
};

const toggleCloneForm = () => {
  showCloneForm.value = !showCloneForm.value;
  if (!showCloneForm.value) resetCloneForm();
};

const submitClone = async () => {
  if (!canSubmit.value) return;
  isCloning.value = true;
  try {
    const { data } = await AccountVoicesAPI.clone(
      cloneName.value.trim(),
      cloneFiles.value
    );
    records.value = [data, ...records.value];
    resetCloneForm();
    showCloneForm.value = false;
    useAlert(t('ACCOUNT_VOICES.CLONE.SUCCESS'));
    startPolling();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('ACCOUNT_VOICES.CLONE.ERROR'));
  } finally {
    isCloning.value = false;
  }
};

// --- excluir ---
const openDeletePopup = voice => {
  selectedVoice.value = voice;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};
const confirmDeletion = async () => {
  closeDeletePopup();
  try {
    await AccountVoicesAPI.delete(selectedVoice.value.id);
    records.value = records.value.filter(
      voice => voice.id !== selectedVoice.value.id
    );
    useAlert(t('ACCOUNT_VOICES.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('ACCOUNT_VOICES.DELETE.ERROR'));
  }
};

onMounted(async () => {
  await fetchVoices();
  startPolling();
});

onBeforeUnmount(() => {
  stopPolling();
  stopRecording();
  stopStream();
  stopAudio();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('ACCOUNT_VOICES.LOADING')"
    :no-records-found="!isLoading && !records.length && !showCloneForm"
    :no-records-message="t('ACCOUNT_VOICES.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('ACCOUNT_VOICES.HEADER')"
        :description="t('ACCOUNT_VOICES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            v-if="!showCloneForm"
            icon="i-lucide-mic"
            :label="t('ACCOUNT_VOICES.NEW_BUTTON')"
            @click="toggleCloneForm"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <!-- Formulário de clonagem -->
      <div
        v-if="showCloneForm"
        class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-5 mb-6"
      >
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ACCOUNT_VOICES.CLONE.NAME_LABEL') }}
          </span>
          <input
            v-model="cloneName"
            type="text"
            :placeholder="t('ACCOUNT_VOICES.CLONE.NAME_PLACEHOLDER')"
            class="h-9 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ACCOUNT_VOICES.CLONE.SAMPLES_LABEL') }}
          </span>

          <!-- Área de arrastar-soltar / clicar -->
          <button
            v-if="!isRecording"
            type="button"
            class="flex w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors"
            :class="
              isDragging
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-slate-8 bg-n-solid-2'
            "
            @click="openFilePicker"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <span class="i-lucide-upload-cloud size-6 text-n-slate-10" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('ACCOUNT_VOICES.CLONE.DROP_HERE') }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t('ACCOUNT_VOICES.CLONE.DROP_FORMATS') }}
            </span>
          </button>

          <!-- Estado de gravação (o momento ao vivo) -->
          <div
            v-else
            class="flex items-center justify-between rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-4 py-3"
          >
            <span
              class="flex items-center gap-2 text-sm font-medium text-n-ruby-11"
            >
              <span
                class="size-2.5 rounded-full bg-n-ruby-9 motion-safe:animate-pulse"
              />
              {{ t('ACCOUNT_VOICES.CLONE.RECORDING') }}
              <span class="tabular-nums">{{ formatTimer(recordSeconds) }}</span>
            </span>
            <Button
              size="sm"
              ruby
              icon="i-lucide-square"
              :label="t('ACCOUNT_VOICES.CLONE.STOP')"
              @click="stopRecording"
            />
          </div>

          <input
            ref="fileInput"
            type="file"
            multiple
            accept="audio/*,video/mpeg,.mp3,.mpeg,.mpga,.ogg,.oga,.opus,.m4a,.aac,.wav,.webm"
            class="hidden"
            @change="onFilesSelected"
          />

          <!-- Botão gravar -->
          <button
            v-if="!isRecording"
            type="button"
            class="flex items-center gap-1.5 self-start rounded-lg px-2 py-1 text-sm font-medium text-n-brand hover:bg-n-brand/10"
            @click="startRecording"
          >
            <span class="i-lucide-mic size-4" />
            {{ t('ACCOUNT_VOICES.CLONE.RECORD') }}
          </button>

          <!-- Chips das amostras -->
          <div v-if="cloneFiles.length" class="flex flex-wrap gap-2">
            <span
              v-for="(file, index) in cloneFiles"
              :key="index"
              class="flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-2 py-1 pl-2.5 pr-1.5 text-xs text-n-slate-12"
            >
              <span
                class="i-lucide-audio-lines size-3.5 shrink-0 text-n-slate-10"
              />
              <span class="max-w-[10rem] truncate">{{ file.name }}</span>
              <span class="text-n-slate-10">{{ formatSize(file.size) }}</span>
              <button
                type="button"
                class="i-lucide-x size-4 rounded text-n-slate-10 hover:text-n-ruby-11"
                :title="t('ACCOUNT_VOICES.CLONE.REMOVE_SAMPLE')"
                @click="removeSample(index)"
              />
            </span>
          </div>

          <p class="text-xs text-n-slate-10">
            {{ t('ACCOUNT_VOICES.CLONE.SAMPLES_HINT') }}
          </p>
        </div>

        <div class="flex justify-end gap-2">
          <Button
            slate
            faded
            :label="t('ACCOUNT_VOICES.CLONE.CANCEL_BUTTON')"
            @click="toggleCloneForm"
          />
          <Button
            :label="
              isCloning
                ? t('ACCOUNT_VOICES.CLONE.CLONING_BUTTON')
                : t('ACCOUNT_VOICES.CLONE.SUBMIT_BUTTON')
            "
            :is-loading="isCloning"
            :disabled="!canSubmit"
            @click="submitClone"
          />
        </div>
      </div>

      <!-- Lista de vozes -->
      <table
        v-if="records.length"
        class="min-w-full divide-y divide-n-weak overflow-x-auto"
      >
        <thead>
          <th
            v-for="th in [
              t('ACCOUNT_VOICES.LIST.TABLE_HEADER.NAME'),
              t('ACCOUNT_VOICES.LIST.TABLE_HEADER.STATUS'),
              t('ACCOUNT_VOICES.LIST.TABLE_HEADER.CREATED_AT'),
              t('ACCOUNT_VOICES.LIST.TABLE_HEADER.ACTIONS'),
            ]"
            :key="th"
            class="py-3 text-left font-semibold text-n-slate-11 ltr:pr-4 rtl:pl-4"
          >
            {{ th }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-12">
          <tr v-for="voice in records" :key="voice.id">
            <td class="py-3 ltr:pr-4 rtl:pl-4">
              <p class="font-medium">{{ voice.name }}</p>
              <p v-if="usageLabel(voice)" class="text-xs text-n-slate-10">
                {{ usageLabel(voice) }}
              </p>
            </td>
            <td class="py-3 ltr:pr-4 rtl:pl-4">
              <span
                class="rounded-full border px-2 py-0.5 text-xs font-medium"
                :class="statusClass(voice.status)"
              >
                {{ statusLabel(voice.status) }}
              </span>
            </td>
            <td class="py-3 text-sm text-n-slate-11 ltr:pr-4 rtl:pl-4">
              {{ formatDate(voice.created_at) }}
            </td>
            <td class="py-3">
              <div class="flex justify-end gap-1">
                <Button
                  v-if="voice.status === 'ready'"
                  v-tooltip.top="t('ACCOUNT_VOICES.LIST.PREVIEW')"
                  size="xs"
                  slate
                  faded
                  :icon="
                    playingId === voice.id ? 'i-lucide-pause' : 'i-lucide-play'
                  "
                  @click="previewVoice(voice)"
                />
                <Button
                  v-tooltip.top="t('ACCOUNT_VOICES.LIST.DELETE')"
                  size="xs"
                  ruby
                  faded
                  icon="i-lucide-trash-2"
                  @click="openDeletePopup(voice)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('ACCOUNT_VOICES.DELETE.CONFIRM.TITLE')"
      :message="t('ACCOUNT_VOICES.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="t('ACCOUNT_VOICES.DELETE.CONFIRM.YES')"
      :reject-text="t('ACCOUNT_VOICES.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
